import Grid from "@mui/material/Grid/Grid";
import {RefObject, useContext, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useRecoilState} from "recoil";
import {useUpsellingRateDetailRepository} from "../../../../api/repositories/UpsellingRateDetailRepository";
import {FormUpsellingRateAssociationModel, FormUpsellingRateModel} from "../../../../api/types/upsellingRate";
import {NewUpsellingRateMatrix, PostUpsellinRateMatrix} from "../../../../api/types/upsellingRateDetail";
import CustomBanner from "../../../../common/components/CustomBanner";
import {FormAutocomplete} from "../../../../common/form-fields";
import {upsellingRateCreationAtom} from "../../../../state/upsellingRatesRecoil";
import {BASE_DEFINITION_FORM_STEPS, SET_SNACKBAR} from "../../../../constants";
import {useAcrissRepository} from "../../../../api/repositories/AcrissRepository";
import {AcrissInterface} from "../../../../api/types/acriss";
import {GlobalContext} from "../../../../../../util/state/GlobalContextProvider";

const UpsellingRateAssociation = ({
                                      submitRef,
                                      refresh
                                  }: { submitRef: RefObject<HTMLButtonElement>, refresh: Function }) => {
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Context
    const {dispatchGlobalData} = useContext(GlobalContext);

    // Use Form Context
    const {register, setValue, handleSubmit, watch} = useFormContext<FormUpsellingRateAssociationModel>();

    // Use Recoil
    const [upsellingRateCreation, setUpsellingRateCreation] = useRecoilState(upsellingRateCreationAtom);
    const {readOnly, inUse, completed, matrixData} = upsellingRateCreation;

    // Use Repository Hooks
    const {getOrderedAcriss} = useAcrissRepository();
    const {postUpsellingRateMatrix} = useUpsellingRateDetailRepository();
    const {getUpsellingRateMatrix} = useUpsellingRateDetailRepository();

    const [reservedCarGroups, setReservedCarGroups] = useState<string[]>([]);
    const [updatedCarGroups, setUpdatedCarGroups] = useState<string[]>([]);

    // Use Effect
    useEffect(() => {
        getStaticData();
        // eslint-disable-next-line
    }, []);

    const getStaticData = async () => {
        try {
            const carGroups: AcrissInterface[] = (await getOrderedAcriss((upsellingRateCreation.definitionData as FormUpsellingRateModel)?.legalEntity?.code as string));

            const reservedCarGroups = [...carGroups];
            reservedCarGroups.splice(reservedCarGroups.length - 1, 1);
            const updatedCarGroups = [...carGroups];
            updatedCarGroups.splice(0, 1);

            setReservedCarGroups([t("pricing:actions.selectAll"), ...reservedCarGroups.map(el => el.acriss)]);
            setUpdatedCarGroups([t("pricing:actions.selectAll"), ...updatedCarGroups.map(el => el.acriss)]);
        } catch (e) {
        }
    };

    useEffect(() => {
        if (matrixData?.matrixColumns?.length && matrixData.matrixColumns.length > 0) {
            setValue("updatedCarGroups", matrixData?.matrixColumns || []);
            setValue("reservedCarGroups", matrixData?.matrixRows.map(el => el.reservedAcriss) || []);
        }
    }, [matrixData]);

    const watchAllForm = watch();

    const createAssociationRequest = ({
                                          reservedCarGroups,
                                          updatedCarGroups
                                      }: FormUpsellingRateAssociationModel): PostUpsellinRateMatrix => {
        return {
            upsellingRateId: upsellingRateCreation?.upsellingRateId || 0,
            reservedAcrissList: reservedCarGroups,
            updatedAcrissList: updatedCarGroups,
            legalEntity: upsellingRateCreation.definitionData?.legalEntity?.code as string
        };
    };

    const confirmAssociation = async (values: FormUpsellingRateAssociationModel) => {
        const request: PostUpsellinRateMatrix = createAssociationRequest(values);
        try {
            const newId = await postUpsellingRateMatrix(request);
            const matrixData: NewUpsellingRateMatrix = await getUpsellingRateMatrix(newId?.id as number);

            setUpsellingRateCreation({
                ...upsellingRateCreation,
                matrixData: matrixData,
                step: BASE_DEFINITION_FORM_STEPS.MATRIX,
                completed: [
                    completed[BASE_DEFINITION_FORM_STEPS.DEFINITION],
                    true,
                    completed[BASE_DEFINITION_FORM_STEPS.MATRIX]
                ],
                newId: newId?.id
            });
            refresh();
            dispatchGlobalData(SET_SNACKBAR.SUCCESS);
        } catch (e) {
        }
    };

    const onChange = (
        input: string[],
        carGroups: string[],
        key: "reservedCarGroups" | "updatedCarGroups"
    ) => {
        const value: string[] =
            // è stato cliccato "selectAll
            input.find((el: string) => el === t("pricing:actions.selectAll")) &&
            // tra i vecchi valori non c'è "selectAll
            !watchAllForm[key]?.find((el: string) => el === t("pricing:actions.selectAll"))
                ? // applica il "selectAll
                carGroups.filter((el: string) => el !== t("pricing:actions.selectAll"))
                : // setta solo i car group selezionati
                input.filter((el) => el !== t("pricing:actions.selectAll"));
        setValue(key, value);
    };

    const onChangeReservedCarGroups = (input: string[]) => {
        onChange(input, reservedCarGroups, "reservedCarGroups");
    };

    const onChangeUpdatedCarGroups = (input: string[]) => {
        onChange(input, updatedCarGroups, "updatedCarGroups");
    };

    return (
        <form onSubmit={handleSubmit(confirmAssociation)}>
            <Grid container spacing={2} alignItems="start">
                {inUse && <CustomBanner message={t("pricing:upsellingRate.banner.association")} severity={"warning"}/>}
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("reservedCarGroups")}
                        label={`${t("pricing:upsellingRate.form.labels.reservedCarGroups")}*`}
                        options={reservedCarGroups}
                        handleChange={onChangeReservedCarGroups}
                        multiple={true}
                        isOptionEqualToValue={(option, value) => option === value}
                        readOnly={readOnly}
                    />
                </Grid>
                <Grid item xs={12} md={3}>
                    <FormAutocomplete
                        {...register("updatedCarGroups")}
                        label={`${t("pricing:upsellingRate.form.labels.updatedCarGroups")}*`}
                        options={updatedCarGroups || []}
                        handleChange={onChangeUpdatedCarGroups}
                        multiple={true}
                        isOptionEqualToValue={(option, value) => option === value}
                        readOnly={readOnly}
                    />
                </Grid>
                <button ref={submitRef} type="submit" style={{display: "none"}}/>
            </Grid>
        </form>
    );
};

export default UpsellingRateAssociation;
